import { ApolloQueryResult, QueryResult } from '@apollo/client';

import { ItemShortType } from 'application/components/SelectableList/types';
import { PointStatusesMap } from 'application/pages/PointsPage/renderer/PointsToolbar/types';

import {
  Exact,
  MediaStreamQuery,
  PaginationInfoType,
  PointQueryFilter,
  PointShortTypeFragment,
  UserFeaturesOnboardingFilter,
  UserFeaturesOnboardingPassMutationInput,
  UserFeaturesOnboardingQueryQuery,
} from 'domain/api/graphql/generated';

export type PointFromLocationType = {
  pointId: string;
  pointAddress: string;
};

export type StreamBindModalFilterType = {
  query?: string;
};

export type DefaultFiltersType = {
  companyIds: string[];
  playingStatuses: PointStatusesMap[];
  companyTagIds: string[];
  query?: string;
};

export type UseStreamBindModalControllerProps = {
  targetStreamId?: string;
  points: PointShortTypeFragment[];
  pointFromLocation?: PointFromLocationType;
  onFilterExecute: (filters: StreamBindModalFilterType) => Promise<void>;
  setPointInput: UseStreamBindModalViewModel['setPointInput'];
};

export type PointListFilterType = {
  pointIds?: Map<string, ItemShortType>;
  initialPointIds?: string[];
};

/* State for View */
export interface StreamBindModalState {
  selectedPoints: Map<string, ItemShortType>;
  disabledPoints: Map<string, ItemShortType>;
  searchText: string;
  isBindLoading: boolean;
}

export enum StreamBindModalActionTypes {
  SET_SELECTED_POINTS = 'setSelectedPoints',
  SET_DISABLED_POINTS = 'setDisabledPoints',
  SET_SEARCH_TEXT = 'setSearchText',
  SET_IS_BIND_LOADING = 'setIsBindLoading',
  RESET_STATE = 'resetState',
}

export type StreamBindModalAction =
  | { type: StreamBindModalActionTypes.SET_SELECTED_POINTS; selectedPoints: Map<string, ItemShortType> }
  | { type: StreamBindModalActionTypes.SET_DISABLED_POINTS; disabledPoints: Map<string, ItemShortType> }
  | { type: StreamBindModalActionTypes.SET_SEARCH_TEXT; searchText: string }
  | { type: StreamBindModalActionTypes.SET_IS_BIND_LOADING; isBindLoading: boolean }
  | { type: StreamBindModalActionTypes.RESET_STATE };

/* State for View Model */
type GetStreamResponseType = Promise<QueryResult<MediaStreamQuery, Exact<{ id: string }>>>;

export interface UseStreamBindModalViewModel {
  streamSetPointsState: StreamSetPointsInputState;
  isReady: boolean;
  isEmpty: boolean;
  isEmptyFiltered: boolean;
  points: PointShortTypeFragment[];
  pointsLoading: boolean;
  paginationInfo?: PaginationInfoType;
  allPointsCount: number;
  isCustomInfoShouldShow: boolean;
  isLoading: boolean;
  userOnboardingFeaturesLoading: boolean;
  isAcceptanceAdvancedPending: boolean;
  onFilterExecute: (filters: PointQueryFilter) => Promise<void>;
  loadMorePoints: () => Promise<void>;
  streamSetToPoints: (streamId: string) => Promise<void>;
  streamSetToPointsLoading: boolean;
  copyTemplateStream: (templateId: string, companyId: string) => Promise<string | undefined>;
  getSavedStreams: (companyIds: string[], isOnlyHifi?: boolean) => Promise<void>;
  getStream: (id: string) => GetStreamResponseType;
  onboardingPassedRequest: (input: UserFeaturesOnboardingPassMutationInput) => Promise<true | undefined>;
  refetchFeaturesOnboarding: (
    variables?:
      | Partial<
          Exact<{
            filters: UserFeaturesOnboardingFilter;
          }>
        >
      | undefined
  ) => Promise<ApolloQueryResult<UserFeaturesOnboardingQueryQuery>>;
  clearCurrentPoint: () => void;
  readStreamWithThematic: () => boolean;
  clearStreamWithThematic: () => void;
  setPointInput: (needAdd: boolean, point: ItemShortType) => void;
}

export enum StreamSetPointsInputActionTypes {
  SET_INITIAL_POINT_IDS = 'setInitialPointIds',
  SET_POINT = 'setPoint',
  RESET_STATE = 'resetState',
}

export type StreamSetPointsInputAction =
  | { type: StreamSetPointsInputActionTypes.SET_INITIAL_POINT_IDS; initialPointIds: string[] }
  | { type: StreamSetPointsInputActionTypes.SET_POINT; needAdd: boolean; point: ItemShortType }
  | { type: StreamSetPointsInputActionTypes.RESET_STATE };

export interface StreamSetPointsInputState {
  filter?: PointListFilterType;
}
